import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  IconNames,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { UnsubscribedHeroOption } from '@library/api-customer-engagement-experience'

const ICON_MAP: Record<string, IconNames> = {
  box: 'box_open',
  refresh: 'resub',
}

const FALLBACK_ICON: IconNames = 'box_open'

const EmptyStateHeroOptionCardButton = withCitrusTheme('button', (theme) => ({
  appearance: 'none',
  alignItems: 'center',
  backgroundColor: theme.color(Color.White),
  border: 'none',
  borderRadius: '4px',
  boxShadow: 'none',
  cursor: 'pointer',
  display: 'flex',
  gap: `${theme.spacing(3)}px`,
  height: '100%',
  padding: `${theme.spacing(3)}px ${theme.spacing(4)}px`,
  position: 'relative',
  textAlign: 'left',
  transition: 'box-shadow 150ms ease-in-out, transform 150ms ease-in-out',
  width: '100%',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 16px -4px rgba(33, 41, 48, 0.08), 0 4px 6px -2px rgba(33, 41, 48, 0.04)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
}))

type EmptyStateHeroOptionCardProps = {
  option: UnsubscribedHeroOption
  onClick: () => void
}

export const EmptyStateHeroOptionCard = ({ option, onClick }: EmptyStateHeroOptionCardProps) => {
  const iconName = ICON_MAP[option.icon] ?? FALLBACK_ICON

  return (
    <EmptyStateHeroOptionCardButton
      data-testid={`empty-state-hero-action-${option.action.intent}`}
      onClick={onClick}
      type="button"
    >
      {option.badge ? (
        <Box
          bg={Color.Success_50}
          paddingH={2}
          paddingV={0.5}
          position={DisplayPosition.Absolute}
          style={{ borderRadius: '4px', top: '12px', right: '12px' }}
        >
          <Text color={Color.Success_900} fontWeight={FontWeight.Bold} size={0}>
            {option.badge}
          </Text>
        </Box>
      ) : null}
      <Box
        alignItems={AlignItems.Center}
        bg={Color.Secondary_100}
        color={Color.Secondary_500}
        display={Display.Flex}
        flexShrink={0}
        height="40px"
        justifyContent={JustifyContent.Center}
        style={{ borderRadius: '50%' }}
        width="40px"
      >
        <Icon aria-hidden="true" name={iconName} size={6} variant={IconVariant.Inherit} />
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1} gap={1}>
        <Text color={Color.ColdGrey_900} fontWeight={FontWeight.Bold} size={[5, 7]}>
          {option.title}
        </Text>
        <Text color={Color.ColdGrey_700} size={[4, 6]}>
          {option.summary}
        </Text>
      </Box>
      <Box
        alignItems={AlignItems.Center}
        color={Color.Secondary_400}
        display={Display.Flex}
        flexShrink={0}
      >
        <Icon aria-hidden="true" name="chevron_right" size={6} variant={IconVariant.Inherit} />
      </Box>
    </EmptyStateHeroOptionCardButton>
  )
}
